import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ConfirmDialog } from './ConfirmDialog.js';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  adfToEditor,
  adfToPlainText,
  editorToAdfOrEmpty,
  type IssueUpdate,
} from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import {
  commentsQuery,
  fieldOptionsQuery,
  issueQuery,
  transitionsQuery,
  useCommentMutation,
  useIssueDelete,
  useIssueUpdate,
  useMarkIssueNotificationsRead,
  useTransitionMutation,
} from '../api/queries.js';
import { formatShortDate, formatUpdated } from '../lib/format.js';
import { AdfEditor, useAdfEditor } from './AdfEditor.js';
import { Avatar, StatusBadge } from './Badges.js';
import { IssueAttachments } from './IssueAttachments.js';
import { IssueChildren } from './IssueChildren.js';
import { IssueHierarchy } from './IssueHierarchy.js';
import styles from './IssueModal.module.css';

/**
 * Карточка задачи.
 *
 * Данные читаются прямо из Jira, а не из кэша таблицы: открытая карточка должна показывать
 * текущее состояние. Перед каждой записью уходит `updated`, на котором началась правка, —
 * если задачу успели изменить, сервер отвечает конфликтом и ничего не перезаписывает.
 */
export function IssueModal({
  issueKey,
  siteUrl,
  onClose,
  onOpenIssue,
}: {
  issueKey: string;
  siteUrl: string;
  onClose: () => void;
  /** Переход к родителю или подзадаче: карточка остаётся открытой и показывает другую задачу. */
  onOpenIssue: (issueKey: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  const issue = useQuery(issueQuery(issueKey));
  const transitions = useQuery(transitionsQuery(issueKey));
  const comments = useQuery(commentsQuery(issueKey));
  const options = useQuery(fieldOptionsQuery(issueKey));

  const update = useIssueUpdate(issueKey);
  const transition = useTransitionMutation(issueKey);
  const comment = useCommentMutation(issueKey);
  const remove = useIssueDelete(issueKey);

  // Открытая карточка гасит уведомления задачи: написанное в ней человек уже видит.
  useMarkIssueNotificationsRead(issueKey);

  const [summary, setSummary] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [commentText, setCommentText] = useState('');
  // Лента показывает последние; более ранние разворачиваются по кнопке, как в макете.
  const [showAllComments, setShowAllComments] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const editor = useAdfEditor(issue.data?.description ?? null);

  useEffect(() => {
    if (issue.data) setSummary(issue.data.summary);
  }, [issue.data?.key, issue.data?.summary]);

  const conflict =
    (update.error instanceof ApiRequestError && update.error.code === 'ISSUE_CONFLICT') ||
    (transition.error instanceof ApiRequestError && transition.error.code === 'ISSUE_CONFLICT');

  /*
   * Подзадачи выясняются только на попытке удаления: спрашивать о них заранее — лишний запрос
   * ради случая, который встречается редко. Число приходит в отказе и меняет текст согласия.
   */
  const deleteError = remove.error instanceof ApiRequestError ? remove.error : null;
  const deleteSubtasks =
    deleteError?.code === 'ISSUE_HAS_SUBTASKS' ? Number(deleteError.params?.['count'] ?? 0) : 0;
  const deleteFailed =
    deleteError && deleteError.code !== 'ISSUE_HAS_SUBTASKS' ? deleteError.code : null;

  function save(changes: Omit<IssueUpdate, 'ifUpdated'>): void {
    const updated = issue.data?.updated;
    if (!updated) return;
    update.mutate({ ...changes, ifUpdated: updated } as IssueUpdate);
  }

  function sendComment(): void {
    const text = commentText.trim();
    if (text.length === 0) return;
    // Тем же конвертером, что и описание: списки, ссылки и код в комментарии тоже работают.
    comment.mutate(editorToAdfOrEmpty({ text, preserved: [] }), {
      onSuccess: () => setCommentText(''),
    });
  }

  const data = issue.data;

  // По макету видны последние четыре, остальные прячутся за кнопкой.
  const allComments = comments.data?.comments ?? [];
  const visibleComments = showAllComments ? allComments : allComments.slice(-4);
  const hiddenComments = allComments.length - visibleComments.length;

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.modal}
          aria-describedby={undefined}
          onEscapeKeyDown={(event) => {
            // Строка создания подзадачи закрывает по Esc себя, а не всю карточку.
            if (event.target instanceof Element && event.target.closest('[data-escape-local]')) {
              event.preventDefault();
            }
          }}
        >
          <header className={styles.header}>
            <span className={`mono ${styles.key}`}>{issueKey}</span>
            {data?.type && (
              <span className={styles.typeGlyph} aria-hidden="true">
                {data.type.name.charAt(0).toUpperCase()}
              </span>
            )}
            <span className={styles.project}>{data?.projectName ?? ''}</span>
            <div className={styles.spacer} />
            <a
              className={styles.jiraLink}
              href={`${siteUrl}/browse/${issueKey}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              {t('issue.openInJira')}
            </a>
            <Dialog.Close asChild>
              <button type="button" className={styles.closeButton} aria-label={t('issue.close')}>
                ✕
              </button>
            </Dialog.Close>
          </header>

          <Dialog.Title className="visually-hidden">
            {data?.summary ?? issueKey}
          </Dialog.Title>

          {issue.isError && (
            <div className={styles.errorState} role="alert">
              {t(
                `errors.${issue.error instanceof ApiRequestError ? issue.error.code : 'INTERNAL'}`,
              )}
            </div>
          )}

          {data && (
            <div className={styles.body}>
              <div className={styles.main}>
                <div className={styles.scroll}>
                  <div className={styles.titleBlock}>
                    {/*
                      Родитель над названием — как цепочка над заголовком в Jira. У подзадачи это
                      задача, у задачи — эпик; щелчок открывает его в этой же карточке.
                    */}
                    {data.parent && (
                      <button
                        type="button"
                        className={styles.parentLink}
                        onClick={() => data.parent && onOpenIssue(data.parent.key)}
                        title={t('issue.openParent', { key: data.parent.key })}
                        aria-label={t('issue.openParent', { key: data.parent.key })}
                      >
                        <ParentIcon />
                        <span className="mono">{data.parent.key}</span>
                        <span className="truncate">{data.parent.summary}</span>
                      </button>
                    )}
                    <input
                      className={styles.summary}
                      value={summary}
                      onChange={(event) => setSummary(event.target.value)}
                      onBlur={() => {
                        if (summary.trim() && summary !== data.summary) save({ summary });
                      }}
                      aria-label={t('table.columns.summary')}
                    />
                  </div>

                  {conflict && (
                    <div className={styles.conflict} role="alert">
                      <span className={styles.conflictTitle}>{t('issue.conflictTitle')}</span>
                      <p className={styles.conflictBody}>{t('issue.conflictBody')}</p>
                      <div className={styles.conflictActions}>
                        <button
                          type="button"
                          className={styles.conflictPrimary}
                          onClick={() => {
                            update.reset();
                            transition.reset();
                            void issue.refetch();
                          }}
                        >
                          {t('issue.conflictTake')}
                        </button>
                      </div>
                    </div>
                  )}

                  <section className={styles.section}>
                    <div className={styles.sectionHead}>
                      <span className="label">{t('issue.description')}</span>
                      {!editingDescription && (
                        <button
                          type="button"
                          className={styles.linkButton}
                          onClick={() => setEditingDescription(true)}
                        >
                          {t('issue.edit')}
                        </button>
                      )}
                    </div>

                    {editingDescription ? (
                      <>
                        <AdfEditor
                          value={editor.document}
                          onChange={editor.setDocument}
                          placeholder={t('issue.descriptionPlaceholder')}
                        />
                        <div className={styles.editActions}>
                          <button
                            type="button"
                            className={styles.primaryButton}
                            disabled={update.isPending}
                            onClick={() => {
                              save({ description: editor.toAdf() });
                              setEditingDescription(false);
                            }}
                          >
                            {t('issue.save')}
                          </button>
                          <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => {
                              editor.reset();
                              setEditingDescription(false);
                            }}
                          >
                            {t('issue.cancel')}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className={styles.description}>
                        {data.renderedDescription ? (
                          // HTML приходит от Jira уже отрисованным — показываем его до правки.
                          <div
                            className={styles.renderedHtml}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: data.renderedDescription }}
                          />
                        ) : (
                          <p className={styles.plain}>
                            {adfToPlainText(data.description) || t('issue.noDescription')}
                          </p>
                        )}
                      </div>
                    )}
                  </section>

                  <IssueAttachments issueKey={issueKey} attachments={data.attachments} />

                  {/* У подзадачи детей не бывает — ниже неё в иерархии Jira ничего нет. */}
                  {data.type?.subtask !== true && (
                    <IssueChildren
                      issue={data}
                      issueTypes={options.data?.issueTypes ?? null}
                      siteUrl={siteUrl}
                      onOpenIssue={onOpenIssue}
                    />
                  )}

                  <section className={styles.section}>
                    <div className={styles.commentsHead}>
                      <span className="label">
                        {t('issue.comments', { count: comments.data?.total ?? 0 })}
                      </span>
                      <span className={styles.rule} aria-hidden="true" />
                    </div>

                    {hiddenComments > 0 && !showAllComments && (
                      <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() => setShowAllComments(true)}
                      >
                        {t('issue.showEarlier', { count: hiddenComments })}
                      </button>
                    )}

                    {visibleComments.map((item) => (
                      <div className={styles.comment} key={item.id}>
                        <Avatar user={item.author} size={26} />
                        <div className={styles.commentBody}>
                          <div className={styles.commentMeta}>
                            <span className={styles.commentAuthor}>
                              {item.author?.displayName ?? '—'}
                            </span>
                            <span className={`mono ${styles.commentTime}`}>
                              {formatUpdated(item.created, t)}
                            </span>
                          </div>
                            <p className={styles.commentText}>{adfToEditor(item.body).text}</p>
                        </div>
                      </div>
                    ))}

                    {comments.data?.comments.length === 0 && (
                      <p className={styles.emptyComments}>{t('issue.noComments')}</p>
                    )}
                  </section>
                </div>

                <div className={styles.commentForm}>
                  <div className={styles.commentInputRow}>
                    <textarea
                      className={styles.commentInput}
                      value={commentText}
                      onChange={(event) => setCommentText(event.target.value)}
                      onKeyDown={(event) => {
                        // Отправка по ⌘↵ — как обещает подпись рядом с кнопкой.
                        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                          event.preventDefault();
                          sendComment();
                        }
                      }}
                      placeholder={t('issue.commentPlaceholder')}
                      rows={2}
                      aria-label={t('issue.commentPlaceholder')}
                    />
                  </div>
                  <div className={styles.commentActions}>
                    <button
                      type="button"
                      className={styles.primaryButton}
                      onClick={sendComment}
                      disabled={comment.isPending || commentText.trim().length === 0}
                    >
                      {t('issue.send')}
                    </button>
                    <span className={`mono ${styles.hint}`}>⌘↵</span>
                  </div>
                </div>
              </div>

              <aside className={styles.side}>
                <div className={styles.field}>
                  <span className="label">{t('table.columns.status')}</span>
                  <div className={styles.statusRow}>
                    <StatusBadge status={data.status} />
                  </div>
                  {(transitions.data?.transitions.length ?? 0) > 0 && (
                    <div className={styles.transitions}>
                      <span className={styles.transitionsLabel}>{t('issue.nextStatus')}</span>
                      {transitions.data?.transitions.map((item) => (
                        <button
                          type="button"
                          key={item.id}
                          className={styles.transitionChip}
                          disabled={transition.isPending}
                          onClick={() => {
                            const updated = data.updated;
                            if (updated) {
                              transition.mutate({ transitionId: item.id, ifUpdated: updated });
                            }
                          }}
                          title={item.hasScreen ? t('issue.transitionHasScreen') : item.name}
                        >
                          {item.name}
                          {item.hasScreen && <span aria-hidden="true"> ↗</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <SelectField
                  label={t('table.columns.type')}
                  value={data.type?.id ?? ''}
                  disabled={update.isPending || (options.data?.readOnly ?? []).includes('issueType')}
                  // Тип подзадачи меняется только на тип подзадачи: превратить её в задачу или задачу
                  // в подзадачу обычной правкой Jira не даёт — это перенос из поля «Родитель».
                  options={(options.data?.issueTypes ?? [])
                    .filter((type) => type.subtask === (data.type?.subtask ?? false))
                    .map((type) => ({ value: type.id, label: type.name }))}
                  fallback={data.type?.name ?? '—'}
                  onChange={(value) => save({ issueTypeId: value })}
                />

                <IssueHierarchy
                  issue={data}
                  issueTypes={options.data?.issueTypes ?? null}
                  onOpenIssue={onOpenIssue}
                />

                <SelectField
                  label={t('table.columns.priority')}
                  value={data.priority?.id ?? ''}
                  disabled={update.isPending}
                  options={(options.data?.priorities ?? []).map((priority) => ({
                    value: priority.id,
                    label: priority.rank ? `P${priority.rank} · ${priority.name}` : priority.name,
                  }))}
                  fallback={data.priority?.name ?? '—'}
                  onChange={(value) => save({ priorityId: value || null })}
                />

                <SelectField
                  label={t('table.columns.assignee')}
                  value={data.assignee?.accountId ?? ''}
                  disabled={update.isPending || (options.data?.readOnly ?? []).includes('assignee')}
                  emptyLabel={t('table.unassigned')}
                  options={(options.data?.assignees ?? []).map((person) => ({
                    value: person.accountId,
                    label: person.displayName,
                  }))}
                  fallback={data.assignee?.displayName ?? t('table.unassigned')}
                  onChange={(value) => save({ assigneeAccountId: value || null })}
                />

                <div className={styles.field}>
                  <span className="label">{t('table.columns.duedate')}</span>
                  <input
                    type="date"
                    className={`mono ${styles.dateInput}`}
                    value={data.duedate ?? ''}
                    disabled={update.isPending}
                    onChange={(event) => save({ duedate: event.target.value || null })}
                    aria-label={t('table.columns.duedate')}
                  />
                </div>

                <ReadOnlyField label={t('table.columns.section')} value={data.projectName} />

                {data.progress !== null && (
                  <div className={styles.field}>
                    <span className="label">{t('issue.progress')}</span>
                    <div className={styles.progressRow}>
                      <div className={styles.progressTrack}>
                        <div className={styles.progressFill} style={{ width: `${data.progress}%` }} />
                      </div>
                      <span className={`mono ${styles.progressValue}`}>{data.progress}%</span>
                    </div>
                  </div>
                )}

                <div className={styles.meta}>
                  {data.planRange && (
                    <div className={styles.metaRow}>
                      <span className={styles.metaLabel}>{t('issue.inPlan')}</span>
                      <span className="mono">
                        {formatShortDate(data.planRange.start)}–{formatShortDate(data.planRange.end)}
                        {' · '}
                        {t('plan.days', { count: data.planRange.markedDays })}
                      </span>
                    </div>
                  )}
                  <div className={styles.metaRow}>
                    <span className={styles.metaLabel}>{t('issue.created')}</span>
                    <span className="mono">{formatShortDate(data.created)}</span>
                  </div>
                  <div className={styles.metaRow}>
                    <span className={styles.metaLabel}>{t('table.columns.updated')}</span>
                    <span className="mono">{formatUpdated(data.updated, t)}</span>
                  </div>
                  {data.reporter && (
                    <div className={styles.metaRow}>
                      <span className={styles.metaLabel}>{t('issue.reporter')}</span>
                      <span className="truncate">{data.reporter.displayName}</span>
                    </div>
                  )}
                </div>

                {/*
                  Удаление стоит последним и отдельно: оно необратимо, и соседство с полями,
                  которые правятся одним щелчком, сделало бы его случайным.
                */}
                <div className={styles.danger}>
                  <button
                    type="button"
                    className={styles.dangerLink}
                    onClick={() => setConfirmingDelete(true)}
                  >
                    {t('issue.delete')}
                  </button>
                </div>
              </aside>
            </div>
          )}
        </Dialog.Content>

        {/*
          Подтверждение удаления — отдельное окно поверх карточки. Раньше оно раскрывалось
          блоком внизу колонки, и при длинной карточке согласие уезжало из поля зрения.
          Число подзадач известно только после первой попытки: сервер отвечает
          ISSUE_HAS_SUBTASKS, и тот же диалог меняет вопрос, не закрываясь.
        */}
        {confirmingDelete && (
          <ConfirmDialog
            title={t('issue.deleteTitle', { key: issueKey })}
            body={
              deleteSubtasks > 0
                ? t('issue.deleteWithSubtasks', { count: deleteSubtasks })
                : t('issue.deleteBody')
            }
            confirmLabel={deleteSubtasks > 0 ? t('issue.deleteAll') : t('issue.deleteConfirm')}
            busy={remove.isPending}
            error={deleteFailed}
            onCancel={() => {
              remove.reset();
              setConfirmingDelete(false);
            }}
            onConfirm={() =>
              remove.mutate({ withSubtasks: deleteSubtasks > 0 }, { onSuccess: onClose })
            }
          />
        )}
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Стрелка «вверх, к родителю» — в той же линии, что значки таблицы. */
function ParentIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path
        d="M12 12.5H7.5A2.5 2.5 0 0 1 5 10V3.5M2.5 6 5 3.5 7.5 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Поле-выбор. Списки значений приходят под конкретную задачу: типы из схемы её проекта,
 * исполнители из тех, кого Jira разрешает назначить именно на неё.
 *
 * Пока список не загрузился — или Jira отказала в правах на него — показывается текущее
 * значение как есть: карточка не должна выглядеть пустой из-за недоступного справочника.
 */
function SelectField({
  label,
  value,
  options,
  fallback,
  emptyLabel,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  fallback: string;
  emptyLabel?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}): React.ReactElement {
  if (options.length === 0) {
    return <ReadOnlyField label={label} value={fallback} />;
  }

  return (
    <div className={styles.field}>
      <span className="label">{label}</span>
      <select
        className={styles.select}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
      >
        {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
        {options.map((option) => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Поле только для чтения: раздел и всё, для чего Jira не дала списка значений. */
function ReadOnlyField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): React.ReactElement {
  return (
    <div className={styles.field}>
      <span className="label">{label}</span>
      <div className={styles.fieldValue}>
        <span className={`${mono ? 'mono' : ''} truncate`}>{value}</span>
      </div>
    </div>
  );
}
